/**
 * AnchoredViewport (Grok model)
 *
 * Wraps `ThreadPrimitive.Viewport`. The chat viewport has exactly TWO
 * auto-scroll states — nothing else may move the scroll position:
 *
 * 1. **Load / conversation switch** — scroll to the bottom so the latest turn
 *    is visible. Held briefly (settle window) while late-rendering content
 *    (markdown, CoT timelines, code blocks) grows the content height, then
 *    released.
 *
 * 2. **New user message** — on `thread.runStart` the new user message is
 *    scrolled to the top of the viewport and **held there**: it is re-pinned on
 *    every layout mutation so new content (CoT, the final answer, late surface
 *    references) expands *below* it without the message ever moving. Re-pinning
 *    is a no-op while content only grows below the message, and a correction
 *    whenever something *above* it reflows.
 *
 *    The pin is NOT released on `thread.runEnd` — a surface/canvas run emits
 *    runEnd early (before the surface reference and the final answer settle),
 *    so a runEnd release would drop the pin mid-response and the late reflow
 *    would clamp-jump the view to the bottom (the reported "jumped to the
 *    bottom after my message was pinned" / "surface chip moved the scroll"
 *    bug). The pin is released only by a genuine user scroll, the
 *    scroll-to-bottom button, or the next load/run.
 *
 * Supporting machinery (creates space; never scrolls by itself):
 *
 * - A **response panel reservation**: the last assistant message gets a
 *   min-height floor filling the viewport below the pinned user message, so
 *   the CoT→answer collapse doesn't reflow anything. While the pin is held the
 *   reservation is kept — releasing it would shrink scrollHeight and the
 *   browser would clamp scrollTop (the bottom-jump above). It is swapped out
 *   naturally when the next turn takes over the panel.
 *
 * - A single **bottom spacer** reserves enough scrollable height for the last
 *   user message to sit at the top: `max(0, clientHeight - heightBelowLastUserMessage)`.
 *   Sized imperatively (no React re-render) so streaming mutations don't race
 *   the scroll position.
 *
 * - While we hold a pin the browser's own scroll anchoring is disabled
 *   (`overflow-anchor: none`) so this component is the sole scroll authority.
 *   Once the user takes over (wheel/touch/scroll-to-bottom button) authority
 *   moves to the BROWSER (`overflow-anchor: auto`): it keeps the reading
 *   position stable across late content and panel resizes, and we stop
 *   scrolling entirely until the next load or run.
 *
 * - The library's auto-scroll / scroll-to-bottom triggers are all disabled.
 *
 * Used by the production `ConversationThread` and the dev `CotPlaybackPage` /
 * `ResumeRunPage` / `ConversationScrollPage` so the dev harnesses exercise the
 * production scroll behavior.
 */

import { useCallback, useContext, useEffect, useRef, type ReactNode } from 'react';
import { useAuiEvent, ThreadPrimitive, useThreadViewportStore } from '@assistant-ui/react';
import { CanvasContext } from '../../contexts/CanvasContext';
import { onInjectedUserTurn } from './injected-turn-signal';

interface AnchoredViewportProps {
  children: ReactNode;
}

/** Small gap left above the pinned user message so it doesn't butt the edge. */
const TOP_ANCHOR_PADDING = 16;

/**
 * Minimum bottom-spacer height kept below the last message so its action bar
 * never docks flush against the composer. The composer footer paints a ~2.25rem
 * gradient fade up over the viewport's bottom edge
 * (`.aui-thread-viewport-footer::before`, index.css); without this floor the
 * spacer collapses to 0 once a turn overflows a screen and the action bar ends
 * up inside that fade (the reported "in the shadow" bug).
 */
const COMPOSER_CLEARANCE = 32;

export function AnchoredViewport({ children }: AnchoredViewportProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const spacerRef = useRef<HTMLDivElement>(null);
  const viewportStore = useThreadViewportStore();

  // The two states. `lockedRef` = state 2 (pin held): engaged at run start,
  // held — re-pinning the latest user message to the top on every layout
  // mutation — until the user takes over or the next load/run. There is
  // intentionally NO runEnd release (see file comment). `initialLoadRef` =
  // state 1 (glued to the bottom during the load settle window). The component
  // remounts per conversation, so these refs reset on conversation switch.
  const lockedRef = useRef(false);
  const initialLoadRef = useRef(true);
  const userScrolledRef = useRef(false); // user took over → stop scrolling entirely
  const reservedAsstRef = useRef<HTMLElement | null>(null); // response panel min-height host
  // Set at runStart to the last user/assistant message NODES at that moment.
  // While set, anchoring is deferred: `thread.runStart` fires BEFORE React
  // commits the new turn, so pinning immediately (or on an unrelated mutation
  // like the followup-suggestions strip unmounting) targets the PREVIOUS
  // user message — a visible jump up to the old turn, then down to the new one
  // once it commits (the reported send "flicker"). Cleared when either node
  // identity changes — a new user message, or a new/replaced assistant message
  // (covers regenerate and resume, which add no user message) — and only then
  // does the pin aim. `wasPinned` records whether the OLD last user message was
  // holding its pin when the run started: if so, the pending window keeps
  // holding it (pre-commit shrinks — a clobbered reservation, the suggestions
  // strip unmounting — would otherwise clamp scrollTop to the new max, painting
  // a "jumped to the bottom" frame right before the new message pins).
  const pendingPinRef = useRef<{
    user: HTMLElement | undefined;
    asst: HTMLElement | undefined;
    wasPinned: boolean;
  } | null>(null);

  const lastUserMessage = useCallback((el: HTMLElement) => {
    const userMessages = el.querySelectorAll('.aui-user-message-root');
    return userMessages[userMessages.length - 1] as HTMLElement | undefined;
  }, []);

  const lastAssistantMessage = useCallback((el: HTMLElement) => {
    const asst = el.querySelectorAll('.aui-assistant-message-root');
    return asst[asst.length - 1] as HTMLElement | undefined;
  }, []);

  // Content-space offset of the last user message's top (i.e. what scrollTop
  // would put it flush with the viewport top). Measured via bounding rects +
  // scrollTop so it's correct regardless of the viewport's `position` (it is
  // `static`, so `offsetTop` would be relative to a page ancestor, not here).
  const lastUserContentTop = useCallback(
    (el: HTMLElement, lastUserMsg: HTMLElement) => {
      const vpRect = el.getBoundingClientRect();
      const msgRect = lastUserMsg.getBoundingClientRect();
      return msgRect.top - vpRect.top + el.scrollTop;
    },
    [],
  );

  // Make the current response (last assistant message) a **full-height panel**
  // that fills the viewport below the pinned user message. The CoT timeline and
  // the final answer then grow/collapse *inside* this stable-height panel — its
  // outer height (a min-height floor ≥ any CoT-phase height) doesn't change when
  // the CoT collapses to "Thought for Xs", so nothing below the user message
  // resizes and there is no scroll at the CoT→answer transition. Only the inner
  // content moves. This is the structural lock; the re-pin below is a backstop.
  //
  // Only steered while the pin is held. When unlocked, an existing reservation
  // is left in place — stripping it would shrink scrollHeight and the browser
  // would clamp scrollTop to the new maximum, visually jumping the view to the
  // bottom. A stale reservation is swapped out when the next turn's run starts.
  const reserveResponsePanel = useCallback(
    (el: HTMLElement) => {
      if (!lockedRef.current) return;
      const lastAsst = lastAssistantMessage(el);
      // Release a stale reservation once a new turn owns the response panel.
      if (reservedAsstRef.current && reservedAsstRef.current !== lastAsst) {
        reservedAsstRef.current.style.minHeight = '';
        reservedAsstRef.current = null;
      }
      const lastUserMsg = lastUserMessage(el);
      if (!lastUserMsg || !lastAsst) return;
      const vpRect = el.getBoundingClientRect();
      const asstTop = lastAsst.getBoundingClientRect().top - vpRect.top + el.scrollTop;
      // Distance from the pinned user message's top edge down to the panel top.
      const offsetBelowAnchor = asstTop - lastUserContentTop(el, lastUserMsg) + TOP_ANCHOR_PADDING;
      // Idempotent write: the observer below watches style mutations (to
      // restore this reservation when a re-render clobbers it), so writing an
      // unchanged value would re-trigger the observer forever.
      const next = `${Math.max(0, el.clientHeight - offsetBelowAnchor)}px`;
      if (lastAsst.style.minHeight !== next) lastAsst.style.minHeight = next;
      reservedAsstRef.current = lastAsst;
    },
    [lastAssistantMessage, lastUserMessage, lastUserContentTop],
  );

  // Resize the bottom spacer so the last user message can be pinned to the top
  // with the viewport filled below it — a backstop for the gap before the
  // response panel mounts (and turns with no assistant message yet). Once the
  // response panel fills the viewport this computes to ~0. Self-correcting:
  // measures content height excluding the spacer's own current height. Set
  // imperatively so streaming mutations don't trigger React re-renders.
  const recomputeSpacer = useCallback(() => {
    const el = viewportRef.current;
    const spacer = spacerRef.current;
    if (!el || !spacer) return;
    reserveResponsePanel(el);
    const lastUserMsg = lastUserMessage(el);
    if (!lastUserMsg) {
      spacer.style.height = '0px';
      return;
    }
    const contentHeight = el.scrollHeight - spacer.offsetHeight;
    let needed: number;
    if (lockedRef.current) {
      // Pin held: reserve enough room below the last user message for it to
      // sit at the viewport top. Floored at COMPOSER_CLEARANCE so the last
      // message's action bar keeps a gap above the composer's gradient fade
      // even once the turn overflows a screen (where this would otherwise
      // collapse to 0 and dock the bar to the edge).
      const belowUser = contentHeight - lastUserContentTop(el, lastUserMsg);
      needed = Math.max(COMPOSER_CLEARANCE, el.clientHeight - belowUser);
    } else {
      // No pin (load / user reading): the reservation exists only to serve the
      // pin, so keep just the composer clearance — a reload then lands at the
      // TRUE bottom of the content instead of the last user message + a blank
      // screen. Never shrink below what the current scrollTop occupies, so
      // this can never clamp-jump the view.
      needed = Math.max(COMPOSER_CLEARANCE, el.scrollTop + el.clientHeight - contentHeight);
    }
    // Idempotent for the same reason as the reservation write above.
    if (Math.abs(spacer.offsetHeight - needed) >= 1) spacer.style.height = `${needed}px`;
  }, [reserveResponsePanel, lastUserMessage, lastUserContentTop]);

  // Pin the last user message to the top of the viewport. No-op when already
  // within a pixel so we don't emit redundant scroll events.
  const anchorToTop = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    const lastUserMsg = lastUserMessage(el);
    if (!lastUserMsg) return;
    const target = Math.max(0, lastUserContentTop(el, lastUserMsg) - TOP_ANCHOR_PADDING);
    // Guarantee the pin can reach its target: mid-commit the spacer can lag a
    // frame behind (measured against content that is about to unmount, e.g.
    // the followup-suggestions strip), leaving max-scroll short of the target
    // and the pinned message settling low, then creeping up. Grow the spacer
    // by the shortfall; the next recomputeSpacer keeps ≥ this by construction.
    const maxScroll = el.scrollHeight - el.clientHeight;
    if (target > maxScroll && spacerRef.current) {
      spacerRef.current.style.height = `${spacerRef.current.offsetHeight + Math.ceil(target - maxScroll)}px`;
    }
    if (Math.abs(target - el.scrollTop) < 1) return;
    // 'instant' — NOT 'auto'. `auto` honors the library's CSS
    // `scroll-behavior: smooth`, so every re-pin animates; during streaming the
    // overlapping animations read as the view bouncing to the bottom and back.
    el.scrollTo({ top: target, behavior: 'instant' });
  }, [lastUserMessage, lastUserContentTop]);

  // The user takes over: stop scrolling entirely until the next load or run,
  // and hand scroll authority to the BROWSER (`overflow-anchor: auto`) so it
  // keeps their reading position stable when late content — e.g. a surface
  // reference — is appended or the panel resizes.
  const releaseToUser = useCallback(() => {
    lockedRef.current = false;
    initialLoadRef.current = false;
    userScrolledRef.current = true;
    if (viewportRef.current) viewportRef.current.style.overflowAnchor = 'auto';
  }, []);

  // STATE 1 — initial load / conversation switch: size the spacer (just the
  // composer clearance — no pin is held, see recomputeSpacer), then scroll to
  // the TRUE bottom of the content so the latest response sits directly above
  // the composer. A single rAF isn't
  // enough — message content (markdown, CoT timelines, code blocks) keeps
  // rendering and growing the content height after this fires, so a one-shot
  // scroll settles short of the real bottom. The onMutate handler below keeps
  // us glued to the bottom while `initialLoadRef` is set; we clear it after a
  // short settle window so a later scrollbar-drag / keyboard scroll back
  // through history (neither fires wheel/touch) isn't yanked to the bottom.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      recomputeSpacer();
      el.scrollTo({ top: el.scrollHeight, behavior: 'instant' });
    });
    const settle = setTimeout(() => {
      initialLoadRef.current = false;
    }, 1000);
    return () => clearTimeout(settle);
  }, [recomputeSpacer]);

  // STATE 2 — new turn: engage (or re-engage) the pin. The actual anchoring is
  // DEFERRED until the new turn's DOM commits (see `pendingPinRef`): runStart
  // fires before React commits the appended message, so an immediate pin would
  // target the previous turn. The observer below aims the pin as soon as the
  // last user/assistant message node changes. The pin then stays on until the
  // user takes over — there is intentionally no runEnd release (see file
  // comment), so `thread.runEnd` is not handled at all.
  const engagePin = useCallback(() => {
    const el = viewportRef.current;
    lockedRef.current = true;
    userScrolledRef.current = false;
    initialLoadRef.current = false;
    if (el) {
      const user = lastUserMessage(el);
      const wasPinned =
        !!user &&
        Math.abs(
          lastUserContentTop(el, user) - TOP_ANCHOR_PADDING - el.scrollTop,
        ) < 2;
      pendingPinRef.current = { user, asst: lastAssistantMessage(el), wasPinned };
    } else {
      pendingPinRef.current = null;
    }
    // We take over scroll: disable the browser's native anchoring so it doesn't
    // fight our instant re-pins.
    if (el) el.style.overflowAnchor = 'none';
  }, [lastUserMessage, lastAssistantMessage, lastUserContentTop]);

  useAuiEvent('thread.runStart', engagePin);

  // Server-injected user turns (credential-continuation nudges, inbox
  // answers) never fire `thread.runStart` — arm the pin through the injected
  // -turn signal instead, so the injected chip pins to the viewport top and
  // the response streams below it exactly like a composer send. Scoped to
  // this thread's conversation so a signal can't grab an unrelated pane.
  const canvasCtx = useContext(CanvasContext);
  const conversationId = canvasCtx?.conversationId;
  useEffect(() => {
    if (!conversationId) return;
    return onInjectedUserTurn((cid) => {
      if (cid === conversationId) engagePin();
    });
  }, [conversationId, engagePin]);

  // A genuine user scroll releases the pin until the next load/run. We detect
  // intent from input events (wheel / touch), NOT from scroll events — layout
  // changes clamp scrollTop and fire scroll events that are not user gestures
  // (mistaking those for a manual scroll would release the pin right at the
  // CoT→answer transition). The scroll-to-bottom button is the third explicit
  // takeover: it fires the viewport store's onScrollToBottom event; without the
  // release, the very next mutation would re-pin and fight the button.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    el.addEventListener('wheel', releaseToUser, { passive: true });
    el.addEventListener('touchmove', releaseToUser, { passive: true });
    const unsubscribe = viewportStore.getState().onScrollToBottom(releaseToUser);
    return () => {
      el.removeEventListener('wheel', releaseToUser);
      el.removeEventListener('touchmove', releaseToUser);
      unsubscribe();
    };
  }, [releaseToUser, viewportStore]);

  // Keep the spacer/response-panel correct as content streams in / the CoT
  // collapses / the viewport resizes, and apply whichever of the two states is
  // active: re-pin the user message (state 2) or glue to the bottom (state 1).
  // Once the user has taken over, do nothing — touching the spacer/panel or
  // scrolling would yank their view when late content like a surface reference
  // lands; the browser's overflow-anchor keeps them stable.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const onLayoutChange = () => {
      if (userScrolledRef.current) return;
      // A pin is armed but the new turn hasn't committed yet. Do NOT re-AIM
      // (anchoring here would target the PREVIOUS user message — the send
      // "flicker"), but DO keep the current geometry stable: pre-commit
      // shrinks (the previous reservation clobbered by a re-render, the
      // suggestions strip unmounting) would otherwise clamp scrollTop to the
      // new max and PAINT a "jumped to the bottom" frame right before the new
      // message pins. recomputeSpacer restores reservation + spacer against
      // the old anchor; if the old pin was actively holding at runStart,
      // keep holding it (a no-op unless a clamp beat us to it).
      if (lockedRef.current && pendingPinRef.current) {
        const pending = pendingPinRef.current;
        const user = lastUserMessage(el);
        const asst = lastAssistantMessage(el);
        if (user === pending.user && asst === pending.asst) {
          recomputeSpacer();
          if (pending.wasPinned) anchorToTop();
          return;
        }
        pendingPinRef.current = null;
      }
      recomputeSpacer();
      if (lockedRef.current) {
        anchorToTop();
      } else if (initialLoadRef.current) {
        el.scrollTo({ top: el.scrollHeight, behavior: 'instant' });
      }
      // else: settled, no state active — never scroll.
    };
    // ── Convergence circuit breaker ──────────────────────────────────────
    // This observer watches style attributes AND its handler writes styles;
    // the design depends on every writer being idempotent so the chain
    // converges. When a browser-specific measurement quirk breaks that
    // invariant (2026-08: Safari, credential-continuation turn injected into
    // a background tab with the pin still held), the observer feeds itself in
    // an unbounded microtask storm — the page wedges black with a dead event
    // loop until reload. Count consecutive handler runs with no macrotask in
    // between: streaming bursts run once per commit/paint cycle and reset via
    // the timeout, so a run of BREAKER_LIMIT without ever reaching a
    // macrotask can only be a non-converging cycle. Trip → disconnect the
    // observers and release scroll authority to the browser for this mount
    // (pin degrades gracefully; next conversation/run remounts fresh).
    const BREAKER_LIMIT = 80;
    let burstCount = 0;
    let burstReset: ReturnType<typeof setTimeout> | null = null;
    let tripped = false;
    const guarded = (fn: () => void) => () => {
      if (tripped) return;
      burstCount += 1;
      if (burstReset === null) {
        burstReset = setTimeout(() => {
          burstCount = 0;
          burstReset = null;
        }, 0);
      }
      if (burstCount > BREAKER_LIMIT) {
        tripped = true;
        obs.disconnect();
        ro.disconnect();
        releaseToUser();
        console.warn(
          '[cred-flow] anchored-viewport: convergence breaker tripped — scroll pinning disabled for this view',
        );
        return;
      }
      fn();
    };

    // CONTENT mutations (new nodes / streamed text) and VIEWPORT resizes share
    // the same policy. Kept as separate observers only because a resize with the
    // user reading back still needs a spacer recompute.
    const onMutate = guarded(onLayoutChange);
    const onResize = guarded(() => {
      if (userScrolledRef.current) {
        recomputeSpacer();
        return;
      }
      onLayoutChange();
    });
    const obs = new MutationObserver(onMutate);
    // `attributes` (style + class only) is load-bearing. style: while the run
    // is starting, a per-second re-render of the running assistant message
    // clobbers the reservation's inline min-height — without watching it, the
    // shrink clamps scrollTop and PAINTS a frame with the pin 90-400px low,
    // restored only at the next content mutation. class: run-end class swaps
    // reflow content ABOVE the anchor without any childList/characterData
    // mutation, leaving the pin sitting ~24px low with nothing left to correct
    // it. Watching both lets the very next microtask restore reservation + pin
    // before paint. All style writers in this file are idempotent (and the
    // anchor no-ops within 1px) so the observer chain converges.
    obs.observe(el, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
    const ro = new ResizeObserver(onResize);
    ro.observe(el);
    return () => {
      if (burstReset !== null) clearTimeout(burstReset);
      obs.disconnect();
      ro.disconnect();
    };
  }, [recomputeSpacer, anchorToTop, lastUserMessage, lastAssistantMessage, releaseToUser]);

  return (
    <ThreadPrimitive.Viewport
      ref={viewportRef}
      className="aui-thread-viewport"
      // flex:1 + min-height:0 (overriding the library's height:100%) so the
      // composer, rendered as a sibling OUTSIDE this scroll viewport, keeps its
      // natural height beneath it and stays fixed — it never scrolls with the
      // chat content. scrollBehavior:auto overrides the library's `smooth` so our
      // instant re-pins don't animate (animated re-pins look like the view
      // bouncing). overflow-anchor is toggled imperatively: 'none' while we hold
      // the pin, 'auto' once the user takes over (browser keeps them stable).
      style={{
        scrollBehavior: 'auto',
        flex: '1 1 0%',
        minHeight: 0,
        height: 'auto',
      }}
      autoScroll={false}
      scrollToBottomOnRunStart={false}
      scrollToBottomOnInitialize={false}
      scrollToBottomOnThreadSwitch={false}
    >
      {children}
      <div aria-hidden ref={spacerRef} style={{ flexShrink: 0, height: 0 }} />
    </ThreadPrimitive.Viewport>
  );
}
